import axios from "axios";
import { CallAPI } from "../../apiManager/ApiCall";
import {
  basicURL,
  lectureLabCreditsURL,
  lectureLabUploadURL,
  lectureLabMaterialsURL,
} from "../../apiManager/APIConfig";
import { store } from "../store";

/*
  ─── Microlab Copilot API (dashboard) ────────────────────────────
  Thin wrappers around the `lecture-lab/*` endpoints on mic-backend.
  Web and mobile share the same ledger / persistence layer — these
  helpers exist purely so dashboard pages don't need to repeat the
  URL composition + auth header dance. Every helper returns the raw
  JSON envelope from the server so the page can branch on `status`
  (200 / 402 insufficient-credits / 404 / 409 etc.) without an
  extra layer of indirection.

  Why two transport mechanisms:
    • `CallAPI` (the XHR-based wrapper in apiManager/ApiCall.js)
      handles every JSON request — it auto-injects the bearer
      token, normalises 401, and never throws for non-2xx. We use
      it everywhere except for file uploads.
    • Multipart uploads need `axios` so we can stream upload
      progress (`onUploadProgress`) and let the browser set the
      multipart boundary itself. This mirrors the pattern used by
      `ai.api.js → generateQuestionsFromMaterialCall`.
*/

/** Resolve the bearer auth header from the persisted Redux store. */
function resolveAuthHeader() {
  const access_token = store.getState().auth?.access_token;
  if (!access_token) return "";
  return access_token.startsWith("Bearer ")
    ? access_token
    : `Bearer ${access_token}`;
}

/** GET /lecture-lab/credits — the shared AI credit balance. */
export const getCopilotCreditsCall = async () => {
  try {
    const response = await CallAPI({
      url: lectureLabCreditsURL,
      method: "GET",
    });
    if (response?.status === 200 && response?.credits) {
      return response.credits;
    }
    return null;
  } catch (_) {
    return null;
  }
};

/**
 * POST /lecture-lab/upload — multipart upload of a lecture material.
 * Uses axios for upload-progress reporting.
 *
 * @param {Object} args
 * @param {File} args.file          required; browser File / Blob
 * @param {string} args.sourceType  'pdf' | 'ppt' | 'image' | 'audio'
 * @param {string} [args.title]     optional override; falls back to file name on the server
 * @param {(pct:number)=>void} [args.onProgress]  0..1 progress callback
 *
 * Returns the raw response. On success the server emits
 *   { status: 200, material: LectureMaterialDto, credits: LectureCreditsDto }
 * On failure surfaces the server JSON verbatim, e.g.
 *   { status: 402, code: 'INSUFFICIENT_CREDITS', credits: { required, creditsRemaining, ... } }
 *   { status: 422, code: 'EXTRACTION_FAILED' }
 */
export const uploadLectureMaterialCall = async ({
  file,
  sourceType,
  title,
  onProgress,
}) => {
  if (!file) return { status: 400, message: "No file provided." };

  const formData = new FormData();
  formData.append("file", file);
  formData.append("source_type", sourceType);
  if (title) formData.append("title", title);

  try {
    const response = await axios.post(
      `${basicURL}${lectureLabUploadURL}`,
      formData,
      {
        headers: { Authorization: resolveAuthHeader() },
        onUploadProgress: (e) => {
          if (e?.total && typeof onProgress === "function") {
            onProgress(e.loaded / e.total);
          }
        },
        // Whisper transcription of long audio + heavy PDFs occasionally
        // approach a couple of minutes server-side; give them headroom.
        timeout: 10 * 60 * 1000,
      },
    );
    return response?.data;
  } catch (err) {
    // axios throws for non-2xx — pass the server envelope through
    // unchanged so the UI can render 402 / 422 differently.
    if (err?.response?.data) return err.response.data;
    return {
      status: err?.response?.status || 0,
      message: err?.message || "Network error during upload.",
    };
  }
};

/** GET /lecture-lab/materials — recent uploads for the current user. */
export const listLectureMaterialsCall = async () => {
  try {
    const response = await CallAPI({
      url: lectureLabMaterialsURL,
      method: "GET",
    });
    if (response?.status === 200 && Array.isArray(response?.materials)) {
      return response.materials;
    }
    return [];
  } catch (_) {
    return [];
  }
};

/** GET /lecture-lab/materials/:id/status — polled while processing. */
export const getLectureMaterialStatusCall = async (id) => {
  if (!id) return null;
  try {
    const response = await CallAPI({
      url: `${lectureLabMaterialsURL}/${id}/status`,
      method: "GET",
    });
    if (response?.status === 200 && response?.material) {
      return response.material;
    }
    return null;
  } catch (_) {
    return null;
  }
};

/** DELETE /lecture-lab/materials/:id — soft delete. */
export const deleteLectureMaterialCall = async (id) => {
  if (!id) return false;
  try {
    const response = await CallAPI({
      url: `${lectureLabMaterialsURL}/${id}`,
      method: "DELETE",
    });
    return response?.status === 200;
  } catch (_) {
    return false;
  }
};

/** GET /lecture-lab/materials/:id/study-pack — full content tree. */
export const getStudyPackCall = async (id) => {
  if (!id) return null;
  try {
    const response = await CallAPI({
      url: `${lectureLabMaterialsURL}/${id}/study-pack`,
      method: "GET",
    });
    return response;
  } catch (_) {
    return null;
  }
};

/** GET /lecture-lab/materials/:id/copilot-chat — chat history. */
export const getCopilotChatHistoryCall = async (id) => {
  if (!id) return { messages: [], supportedIntents: [] };
  try {
    const response = await CallAPI({
      url: `${lectureLabMaterialsURL}/${id}/copilot-chat`,
      method: "GET",
    });
    if (response?.status === 200) {
      return {
        messages: Array.isArray(response.messages) ? response.messages : [],
        supportedIntents: Array.isArray(response.supportedIntents)
          ? response.supportedIntents
          : [],
      };
    }
    return { messages: [], supportedIntents: [] };
  } catch (_) {
    return { messages: [], supportedIntents: [] };
  }
};

/**
 * POST /lecture-lab/materials/:id/copilot-chat — the core Copilot chat send.
 * Charges 1 credit per call regardless of intent. The server appends both
 * the user and assistant messages into `study_pack.copilotMessages`, runs
 * the intent-specific generator (extra quizzes, extra flashcards, deepen
 * chart, free-form answer), and returns the updated pack.
 *
 * @param {Object} args
 * @param {string} args.materialId
 * @param {string} args.message      free-text prompt (required for chat_answer)
 * @param {'chat_answer'|'extra_quizzes'|'extra_flashcards'|'deepen_study_chart'} args.intent
 * @param {Object} [args.options]   optional intent-specific options (e.g. { count: 5 })
 *
 * Returns the raw response. 402 → insufficient credits.
 */
export const sendCopilotChatCall = async ({
  materialId,
  message,
  intent,
  options,
}) => {
  if (!materialId) return { status: 400, message: "Missing materialId." };
  try {
    const response = await CallAPI({
      url: `${lectureLabMaterialsURL}/${materialId}/copilot-chat`,
      method: "POST",
      body: {
        message: message || "",
        intent: intent || "chat_answer",
        options: options || undefined,
      },
    });
    return response;
  } catch (_) {
    return { status: 400, message: "Failed to send chat message." };
  }
};

/** GET /lecture-lab/materials/:id/study-chart — heuristic topics graph (free). */
export const getStudyChartCall = async (id) => {
  if (!id) return null;
  try {
    const response = await CallAPI({
      url: `${lectureLabMaterialsURL}/${id}/study-chart`,
      method: "GET",
    });
    if (response?.status === 200 && response?.studyChart) {
      return response.studyChart;
    }
    return null;
  } catch (_) {
    return null;
  }
};

/*
  ─── Shared cost helpers ─────────────────────────────────────────
  Mirror the server-side `services/lectureLab/aiCredits.service.js`
  cost map. We keep a static fallback for the moment when the user
  has never fetched their credits yet (e.g. first paint of the
  hub) so cost previews render immediately. The real numbers are
  resolved from `credits.costs.*` once `getCopilotCreditsCall()`
  has returned.
*/
export const STATIC_COPILOT_COSTS = Object.freeze({
  small_pdf: 5,
  large_pdf: 10,
  powerpoint: 12,
  audio: 15,
  image: 5,
  ai_question_image: 2,
  copilot_chat: 1,
});

/** Pick the upload cost for a `sourceType + fileSize` pair. */
export function uploadCostForKind(sourceType, fileSizeBytes, costs = STATIC_COPILOT_COSTS) {
  const c = costs || STATIC_COPILOT_COSTS;
  switch (sourceType) {
    case "audio":
      return c.audio ?? 15;
    case "ppt":
      return c.powerpoint ?? 12;
    case "image":
      return c.image ?? 5;
    case "pdf": {
      // Mobile draws the line at 4 MB → large pdf. We mirror that
      // so the cost preview on the hub matches what the user sees
      // after the upload succeeds.
      const isLarge =
        typeof fileSizeBytes === "number" && fileSizeBytes >= 4 * 1024 * 1024;
      return isLarge ? c.large_pdf ?? 10 : c.small_pdf ?? 5;
    }
    default:
      return 0;
  }
}
